import { ChevronRight } from 'lucide-react';
import { Fragment, useCallback, useState, type ReactNode } from 'react';
import sheet from '../../../components/SheetTable.module.css';
import { formatNumber } from '../../../lib/format';
import type { OrgTreeNode, RollupCounts } from '../api/organisation-api';
import { SEAT_SEGMENTS, SeatMix } from './SeatMix';
import styles from './OrgTree.module.css';

/**
 * ORG-01 — the org tree, drawn as one table (Idea B, DECISIONS-LOG 2026-10-25).
 *
 * Ventures with seats are rows with their departments and teams indented under them;
 * ventures with no seats yet collapse into one line under the table, so empty rows of
 * dashes no longer bury the units that have something to show.
 *
 * Clicking a unit's name sets the application's scope, which is the point of the screen —
 * S5: "clicking an org tree node filters the ENTIRE application to that unit."
 */

export interface OrgTreeProps {
  readonly nodes: readonly OrgTreeNode[];
  /** The node the whole application is currently filtered to, if any. */
  readonly selectedId: string | null;
  onSelect(node: OrgTreeNode): void;
}

export function OrgTree({ nodes, selectedId, onSelect }: OrgTreeProps): ReactNode {
  // Ventures open by default. A tree that starts fully collapsed makes the first
  // click a chore on a screen whose whole job is showing where the holes are.
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());

  const toggle = useCallback((id: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const withSeats = nodes.filter((node) => node.counts.total > 0);
  const withoutSeats = nodes.filter((node) => node.counts.total === 0);

  return (
    <div className={styles.tree}>
      {withSeats.length > 0 && (
        <div className={styles.scroll}>
          <table className={`${sheet.sheet} ${styles.table}`} aria-label="Organisation">
            <thead>
              <tr>
                <th>Unit</th>
                <th className={styles.mixCol}>Seat mix</th>
                <th className={styles.countCol}>Seats</th>
                {SEAT_SEGMENTS.map((segment) => (
                  <th key={segment.key} className={styles.countCol}>
                    {segment.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {withSeats.map((node) => (
                <UnitRows
                  key={node.id}
                  node={node}
                  depth={0}
                  collapsed={collapsed}
                  onToggle={toggle}
                  selectedId={selectedId}
                  onSelect={onSelect}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {withoutSeats.length > 0 && (
        <div className={styles.empties}>
          <span className={styles.emptiesLabel}>
            {withoutSeats.length === 1 ? '1 venture has' : `${formatNumber(withoutSeats.length)} ventures have`} no
            seats yet:
          </span>
          {withoutSeats.map((node) => (
            <button
              key={node.id}
              type="button"
              className={`${styles.emptyName} ${node.id === selectedId ? styles.emptyNameSelected : ''}`}
              onClick={() => onSelect(node)}
            >
              {node.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface UnitRowsProps {
  readonly node: OrgTreeNode;
  readonly depth: number;
  readonly collapsed: ReadonlySet<string>;
  onToggle(id: string): void;
  readonly selectedId: string | null;
  onSelect(node: OrgTreeNode): void;
}

function UnitRows({ node, depth, collapsed, onToggle, selectedId, onSelect }: UnitRowsProps): ReactNode {
  const hasChildren = node.children.length > 0;
  const isOpen = hasChildren && !collapsed.has(node.id);
  const isSelected = node.id === selectedId;
  const rowClass = [depth === 0 ? styles.venture : '', isSelected ? styles.rowSelected : ''].filter(Boolean).join(' ');

  return (
    <Fragment>
      <tr className={rowClass} aria-selected={isSelected}>
        <td>
          <span
            className={styles.unit}
            // The indent is measured geometry, which is the one thing an inline style
            // is for here (S2 convention). Appearance stays in the stylesheet.
            style={{ '--depth': depth } as React.CSSProperties}
          >
            <button
              type="button"
              className={`${styles.twisty} ${isOpen ? styles.twistyOpen : ''}`}
              onClick={() => onToggle(node.id)}
              aria-label={isOpen ? `Collapse ${node.name}` : `Expand ${node.name}`}
              aria-expanded={hasChildren ? isOpen : undefined}
              // A leaf keeps the button's width so names stay in one column, but
              // nothing to press: a disabled control that does nothing is noise.
              disabled={!hasChildren}
            >
              {hasChildren && <ChevronRight size={14} aria-hidden />}
            </button>
            <button type="button" className={styles.name} onClick={() => onSelect(node)}>
              <span className={styles.nameText}>{node.name}</span>
              {node.code !== null && <span className={styles.code}>{node.code}</span>}
            </button>
          </span>
        </td>
        <td className={styles.mixCol}>
          <SeatMix counts={node.counts} />
        </td>
        <Counts counts={node.counts} />
      </tr>

      {isOpen &&
        node.children.map((child) => (
          <UnitRows
            key={child.id}
            node={child}
            depth={depth + 1}
            collapsed={collapsed}
            onToggle={onToggle}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        ))}
    </Fragment>
  );
}

/**
 * The total and the five states. A zero renders as a faint dash rather than "0" —
 * 06-qol §11's empty rule — so the eye finds the department with four vacancies at a
 * glance instead of reading a wall of noughts.
 */
function Counts({ counts }: { readonly counts: RollupCounts }): ReactNode {
  return (
    <>
      <Count value={counts.total} label="seats" isTotal />
      {SEAT_SEGMENTS.map((segment) => (
        <Count key={segment.key} value={counts[segment.key]} label={segment.label.toLowerCase()} />
      ))}
    </>
  );
}

interface CountProps {
  readonly value: number;
  readonly label: string;
  readonly isTotal?: boolean;
}

function Count({ value, label, isTotal }: CountProps): ReactNode {
  const className = [styles.countCol, styles.count, isTotal ? styles.countTotal : '', value === 0 ? styles.countZero : '']
    .filter(Boolean)
    .join(' ');
  return (
    <td className={className} title={`${formatNumber(value)} ${label}`}>
      {value === 0 ? '—' : formatNumber(value)}
    </td>
  );
}
